import React, { useState, useMemo } from 'react';
import { Modal, Form, Select, Input, App } from 'antd';

import { logger } from '../../services/logger.client';
import type { NPC, Monster, PartyMember, Deity, EntityType } from '../../types/types';
const { Option } = Select;

interface AddRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  npcs: NPC[];
  monsterRoster: Monster[];
  partyRoster: PartyMember[];
  deities: Deity[];
  onAddRelationship: (
    entity1Id: number,
    entity1Type: EntityType,
    entity2Id: number,
    entity2Type: EntityType,
    type: string,
  ) => Promise<boolean>;
}

const AddRelationshipModal: React.FC<AddRelationshipModalProps> = ({
  isOpen,
  onClose,
  npcs,
  monsterRoster,
  partyRoster,
  deities,
  onAddRelationship,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const creatureOptions = useMemo(() => {
    const allCreatures = [
      ...(partyRoster ?? []).map((p: PartyMember) => ({ id: p.id, name: p.name, type: 'player' as const })),
      ...(npcs ?? []).map((n: NPC) => ({ id: n.id, name: n.name, type: 'npc' as const })),
      ...(monsterRoster ?? []).map((m: Monster) => ({ id: m.id, name: m.name, type: 'monster' as const })),
      ...(deities ?? []).map((d: Deity) => ({ id: d.id, name: d.name, type: 'deity' as const })),
    ].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    return allCreatures;
  }, [partyRoster, npcs, monsterRoster, deities]);

  const handleOk = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      const { entity1Id, entity2Id, type } = values;

      const entity1 = (creatureOptions ?? []).find(c => c.id === entity1Id);
      const entity2 = (creatureOptions ?? []).find(c => c.id === entity2Id);

      if (!entity1 || !entity2) {
        message.error('Could not find one of the selected entities.');
        return;
      }

      if (entity1.id === entity2.id && entity1.type === entity2.type) {
        message.error('An entity cannot have a relationship with themselves.');
        return;
      }

      const success = await onAddRelationship(entity1.id, entity1.type, entity2.id, entity2.type, type);
      if (success) {
        message.success('Relationship has been added.');
        form.resetFields();
        onClose();
      } else {
        message.warning('This relationship already exists.');
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'info' in error) {
        // Antd form validation error
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      message.error(`Failed to add relationship: ${errorMessage}`);
      logger.warn({ formValues: form.getFieldsValue(), errorInfo: error }, 'Add Relationship failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add Relationship"
      confirmLoading={loading}
      open={isOpen}
      onCancel={onClose}
      onOk={handleOk}
      okText="Add Relationship"
      destroyOnHidden
    >      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} name="add_relationship_form">
        <Form.Item name="entity1Id" label="From Entity" rules={[{ required: true }]}>
          <Select showSearch optionFilterProp="children" placeholder="Select first NPC" allowClear>
            {(creatureOptions ?? []).map(c => <Option key={c.id} value={c.id}>{c.name} ({c.type})</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="type" label="Relationship" rules={[{ required: true }]}>
          <Input placeholder="e.g., is the sibling of" />
        </Form.Item>
        <Form.Item name="entity2Id" label="To Entity" rules={[{ required: true }]}>
          <Select showSearch optionFilterProp="children" placeholder="Select second NPC" allowClear>
            {(creatureOptions ?? []).map(c => <Option key={c.id} value={c.id}>{c.name} ({c.type})</Option>)}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddRelationshipModal;